import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useParams, useNavigate } from "react-router-dom";
import attributeActions from "../../../../store/actions/items/attributes/attribute.actions";

export const AttributeValuesPageComponent = ({ attributeActions }) => {
  const { attributeId } = useParams(); // Get attributeId from URL
  const navigate = useNavigate();

  const [attributeValues, setAttributeValues] = useState([]); // Store attribute values
  const [attributeName, setAttributeName] = useState(""); // Store the name of the attribute
  const [newValue, setNewValue] = useState(""); // Store new attribute value to add
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch attribute values and name when the page loads
  useEffect(() => {
    fetchAttributeDetails();
  }, [attributeId]);

  const fetchAttributeDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch attribute details (to get the attribute name)
      const attributeResponse = await attributeActions.getAttributeById(attributeId);
      setAttributeName(attributeResponse.payload.name);

      // Fetch attribute values
      const valuesResponse = await attributeActions.getAttributeValues(attributeId);
      setAttributeValues(valuesResponse.payload);
    } catch (error) {
      console.error("Error fetching attribute details:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddValue = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const newAttributeValue = {
        attributeId, // Required ID of the parent attribute
        value: newValue, // New value entered by the user
      };

      // Send request to add the new attribute value
      const response = await attributeActions.addAttributeValue(newAttributeValue);
      await fetchAttributeDetails();
      // Update the UI with the new attribute value
      setNewValue(""); // Clear the input field
    } catch (error) {
      console.error("Error adding attribute value:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteValue = async (valueId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this value?");
    if (!confirmDelete) return;

    setIsLoading(true);
    setError(null);

    try {
      await attributeActions.deleteAttributeValue(valueId);
      // Update the list by filtering out the deleted value
      setAttributeValues((prev) => prev.filter((value) => value.id !== valueId));
    } catch (error) {
      console.error("Error deleting attribute value:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToAttributes = () => {
    navigate("/attributes");
  };

  return (
    <div>
      <h2>Attribute Values for: {attributeName}</h2>
      <button onClick={handleBackToAttributes}>Back to Attributes</button>

      {isLoading ? (
        <p>Loading...</p>
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <>
          <form onSubmit={handleAddValue}>
            <input type="text" placeholder="Enter new value" value={newValue} onChange={(e) => setNewValue(e.target.value)} required />
            <button type="submit">Add Value</button>
          </form>

          {attributeValues.length === 0 ? (
            <p>No values found for this attribute.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attributeValues.map((value) => (
                  <tr key={value.id}>
                    <td>{value.value}</td>
                    <td>
                      <button onClick={() => handleDeleteValue(value.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    attributeActions: bindActionCreators(attributeActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(AttributeValuesPageComponent);
